import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { showToast } from "../../store/uiSlice";
import { adminApi } from "../../services/api";
import UserDetailModal from "./UserDetailModal";
import { Softphone } from "@frejun/softphone-web-sdk";

const STATUS_OPTIONS = [
  "new_lead",
  "pending",
  "call_back",
  "potential_client",
  "low_potential_client",
  "not_interested",
  "wrong_number",
  "language_irrelevant",
  "no_answer",
  "underage",
  "ftd",
  "not_registered",
  "on_call",
];

const LEADS_PER_PAGE = 40;

// ─── PHONE NORMALIZATION ─────────────────────────────────────────────────────
const normalizePhone = (raw) => {
  if (!raw) return raw;
  let s = String(raw).trim();
  s = s.replace(/[^\d+]/g, "");
  s = s.replace(/(?!^)\+/g, "");
  if (s.startsWith("00")) {
    s = "+" + s.slice(2);
  }
  if (!s.startsWith("+")) {
    s = "+" + s;
  }
  return s;
};
// ─────────────────────────────────────────────────────────────────────────────

const StaffLeadPanel = () => {
  const dispatch = useDispatch();
  const { adminUser } = useSelector((state) => state.admin);
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [commentInputs, setCommentInputs] = useState({});
  const [statusInputs, setStatusInputs] = useState({});
  const [customStatus, setCustomStatus] = useState({});
  const [updating, setUpdating] = useState({});
  const [expandedLead, setExpandedLead] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResult, setSearchResult] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);

  const [statusFilter, setStatusFilter] = useState("all");
  const [startDateFilter, setStartDateFilter] = useState("");
  const [endDateFilter, setEndDateFilter] = useState("");

  // ─── EMAIL STATE ───
  const [emailSenderOptions, setEmailSenderOptions] = useState([]);
  const [showSenderSelect, setShowSenderSelect] = useState(false);
  const [selectedSender, setSelectedSender] = useState(null);
  const [showComposeEmail, setShowComposeEmail] = useState(false);
  const [composeLeadName, setComposeLeadName] = useState("");
  const [composeSubject, setComposeSubject] = useState("");
  const [composeBody, setComposeBody] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [targetLeadEmail, setTargetLeadEmail] = useState(null);
  const fileInputRef = useRef(null);

  // ─── FREJUN SOFT PHONE STATE ───
  const [isCallActive, setIsCallActive] = useState(false);
  const [incomingCall, setIncomingCall] = useState(null);
  const softphoneRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const localAudioRef = useRef(null);
  const incomingTimeoutRef = useRef(null);
  const ringtoneRef = useRef(null);

  const FREJUN_TOKEN = import.meta.env.VITE_FREJUN_TOKEN;
  const FREJUN_EMAIL =
    adminUser?.frejunEmail ||
    import.meta.env.VITE_FREJUN_EMAIL ||
    "finance@groxion.com";
  const AGENT_VIRTUAL_NUMBER = adminUser?.frejunNumber || null;

  const canSeeFullDetails = () => {
    if (!adminUser) return false;
    return adminUser.role === "admin" || adminUser.role === "super_admin";
  };

  // Helper to check if a lead belongs to the new retention table
  const isRetentionData = (lead) => {
    if (!lead) return false;
    return lead.source === "RetentionData_Upload" || (lead.short_id && lead.short_id.startsWith("RET"));
  };

  useEffect(() => {
    fetchLeads();
    fetchEmailCredentials();
  }, []);

  // ─── RINGTONE SETUP & UNLOCK ───
  useEffect(() => {
    ringtoneRef.current = new Audio("/the_fire_love.mp3");
    ringtoneRef.current.loop = true;
    ringtoneRef.current.preload = "auto";

    const unlockAudio = () => {
      if (ringtoneRef.current) {
        ringtoneRef.current.volume = 0;
        ringtoneRef.current
          .play()
          .then(() => {
            ringtoneRef.current.pause();
            ringtoneRef.current.currentTime = 0;
            ringtoneRef.current.volume = 1;
          })
          .catch((e) => console.warn("[Audio] Unlock blocked by browser:", e));
      }
      document.removeEventListener("click", unlockAudio);
    };

    document.addEventListener("click", unlockAudio);
    return () => document.removeEventListener("click", unlockAudio);
  }, []);

  const playRingtone = () => {
    if (ringtoneRef.current) {
      ringtoneRef.current.currentTime = 0;
      ringtoneRef.current.play().catch((e) => {
        console.error("[Audio] Play failed:", e);
        dispatch(
          showToast({
            message: "Please click anywhere to allow ringtone audio.",
            type: "warning",
          }),
        );
      });
    }
  };

  const stopRingtone = () => {
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
    }
  };

  const clearIncoming = () => {
    if (incomingTimeoutRef.current) clearTimeout(incomingTimeoutRef.current);
    incomingTimeoutRef.current = null;
    stopRingtone();
    setIncomingCall(null);
  };

  // ─── INIT FREJUN SOFT PHONE ───
  useEffect(() => {
    if (!FREJUN_TOKEN || !adminUser?.frejunEmail) return;

    let isMounted = true;
    const initSoftphone = async () => {
      try {
        const softphone = new Softphone();
        await softphone.login({
          type: "OAuth2.0",
          token: FREJUN_TOKEN,
          email: FREJUN_EMAIL,
        });
        if (!isMounted) return;
        softphoneRef.current = softphone;

        const listeners = {
          onConnectionStateChange: (type, newState) => {},
          onCallCreated: (type, details) => {
            if (type === "Incoming") {
              setIncomingCall({ lead: null });
              playRingtone();

              let callerNumber =
                details?.candidate || details?.request?.from?.uri?.user || "";
              if (callerNumber.includes("sip:")) {
                callerNumber = callerNumber.split("sip:")[1].split("@")[0];
              }

              if (incomingTimeoutRef.current)
                clearTimeout(incomingTimeoutRef.current);
              incomingTimeoutRef.current = setTimeout(
                () => setIncomingCall(null),
                60000,
              );

              if (callerNumber) {
                adminApi
                  .post("/leads/lookup-phone", { phone: callerNumber })
                  .then((res) => {
                    if (res.data?.found) {
                      setIncomingCall((prev) =>
                        prev ? { lead: res.data.lead } : prev,
                      );
                    }
                  })
                  .catch((err) => console.error("Lookup Failed:", err));
              }
            } else if (type === "Outgoing") {
              setIsCallActive(true);
            }
          },
          onCallRinging: () => {},
          onCallHangup: () => {
            setIsCallActive(false);
            clearIncoming();
          },
        };

        const audioElements = {
          remote: remoteAudioRef.current,
          local: localAudioRef.current,
        };
        await softphone.start(listeners, audioElements, {
          endSessionOnNavigate: false,
          logoutOnNavigate: false,
        });
      } catch (err) {
        console.error("[Softphone] init failed:", err);
        if (isMounted)
          dispatch(
            showToast({ message: "Dialer init failed.", type: "error" }),
          );
      }
    };

    initSoftphone();
    return () => {
      isMounted = false;
      softphoneRef.current?.logout();
      softphoneRef.current = null;
      stopRingtone();
    };
  }, [FREJUN_TOKEN, FREJUN_EMAIL, adminUser?.frejunEmail, dispatch]);

  const handleFrejunCall = async (phoneNumber, leadName) => {
    if (!phoneNumber || !softphoneRef.current) {
      dispatch(showToast({ message: "Dialer not ready.", type: "error" }));
      return;
    }
    const normalizedNumber = normalizePhone(phoneNumber);
    try {
      setIsCallActive(true);
      await softphoneRef.current.makeCall(
        normalizedNumber,
        AGENT_VIRTUAL_NUMBER || undefined,
        {},
      );
    } catch (err) {
      setIsCallActive(false);
      dispatch(showToast({ message: "Call failed.", type: "error" }));
    }
  };

  const acceptCall = async () => {
    stopRingtone();
    try {
      await softphoneRef.current?.getSession?.accept();
      setIncomingCall(null);
      setIsCallActive(true);
    } catch (err) {
      dispatch(showToast({ message: "Accept failed", type: "error" }));
    }
  };

  const declineCall = async () => {
    stopRingtone();
    clearIncoming();
    setIsCallActive(false);
    try {
      await softphoneRef.current?.getSession?.end();
    } catch (e) {}
  };

  const handleHangup = async () => {
    try {
      await softphoneRef.current?.getSession?.end();
    } catch (e) {}
    setIsCallActive(false);
  };

  const fetchLeads = async () => {
    setIsLoading(true);
    setSearchResult(null);
    setSearchQuery("");
    try {
      const res = await adminApi.get("/leads/my");
      setLeads(res.data?.leads || []);
    } catch (e) {
      dispatch(showToast({ message: "Failed to load leads", type: "error" }));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchEmailCredentials = async () => {
    try {
      const res = await adminApi.get("/admin/email/credentials");
      setEmailSenderOptions(res.data?.emailOptions || []);
    } catch (e) {}
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    const query = searchQuery.trim();
    if (!query) {
      setSearchResult(null);
      return;
    }
    setIsSearching(true);
    setSearchResult(null);
    try {
      try {
        const res = await adminApi.get(`/leads/short/${query.toUpperCase()}`);
        if (res.data?.lead) {
          setSearchResult([res.data.lead]);
          setCurrentPage(1);
          setIsSearching(false);
          return;
        }
      } catch (e) {}
      const q = query.toLowerCase();
      const filtered = leads.filter(
        (lead) =>
          (lead.name && lead.name.toLowerCase().includes(q)) ||
          (lead.email && lead.email.toLowerCase().includes(q)) ||
          (lead.phone && lead.phone.toLowerCase().includes(q)) ||
          (lead.short_id && lead.short_id.toLowerCase().includes(q)),
      );
      setSearchResult(filtered);
      setCurrentPage(1);
    } catch (e) {
      dispatch(showToast({ message: "Search failed", type: "error" }));
    } finally {
      setIsSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSearchResult(null);
    setCurrentPage(1);
  };

  const displayLeads = (searchResult !== null ? searchResult : leads).filter(
    (lead) => {
      if (statusFilter !== "all" && lead.status !== statusFilter) return false;
      if (lead.createdAt) {
        const leadDate = new Date(lead.createdAt).toISOString().split("T")[0];
        if (startDateFilter && leadDate < startDateFilter) return false;
        if (endDateFilter && leadDate > endDateFilter) return false;
      }
      return true;
    },
  );

  const totalPages = Math.ceil(displayLeads.length / LEADS_PER_PAGE);
  const paginatedLeads = displayLeads.slice(
    (currentPage - 1) * LEADS_PER_PAGE,
    currentPage * LEADS_PER_PAGE,
  );

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, startDateFilter, endDateFilter, searchResult]);

  // ─── UPDATE HANDLER (UPDATED FOR RETENTION DATA) ───
  const handleUpdate = async (leadId) => {
    const lead = leads.find((l) => l.id === leadId);
    const comment = commentInputs[leadId] || "";
    const dropdownStatus = statusInputs[leadId] || "";
    const custom = customStatus[leadId] || "";
    const status = custom || dropdownStatus || null;
    
    if (!comment && !status) {
      dispatch(
        showToast({
          message: "Add a comment or select a status",
          type: "error",
        }),
      );
      return;
    }

    setUpdating({ ...updating, [leadId]: true });
    try {
      // Route to the correct endpoint based on the lead source
      const url = isRetentionData(lead)
        ? `/leads/retentiondata/${leadId}/status`
        : `/leads/${leadId}/status`;

      const res = await adminApi.put(url, { comment, status });
      
      if (res.data?.lead) {
        setLeads((prev) =>
          prev.map((l) => (l.id === leadId ? res.data.lead : l)),
        );
      }
      dispatch(showToast({ message: "Lead updated", type: "success" }));
      setCommentInputs({ ...commentInputs, [leadId]: "" });
      setStatusInputs({ ...statusInputs, [leadId]: "" });
      setCustomStatus({ ...customStatus, [leadId]: "" });
    } catch (e) {
      dispatch(showToast({ message: "Failed to update", type: "error" }));
    } finally {
      setUpdating({ ...updating, [leadId]: false });
    }
  };

  // ─── VIEW LINKED USER (UPDATED FOR RETENTION DATA) ───
  const handleViewLinkedUser = async (leadId) => {
    const lead = leads.find((l) => l.id === leadId);
    try {
      // Route to the correct endpoint based on the lead source
      const url = isRetentionData(lead)
        ? `/leads/retentiondata/${leadId}/user`
        : `/leads/${leadId}/user`;

      const res = await adminApi.get(url);
      if (res.data?.user) {
        setSelectedUser(res.data.user);
        setShowUserModal(true);
      }
    } catch (e) {
      dispatch(
        showToast({ message: "No user linked to this lead", type: "error" }),
      );
    }
  };

  const handleDialNumber = async (leadId, e) => {
    e.stopPropagation();
    if (!AGENT_VIRTUAL_NUMBER) {
      dispatch(
        showToast({ message: "No virtual number assigned.", type: "error" }),
      );
      return;
    }
    try {
      const res = await adminApi.get(`/leads/${leadId}/dial`);
      if (res.data?.phone) {
        const lead = leads.find((l) => l.id === leadId);
        handleFrejunCall(
          normalizePhone(res.data.phone),
          lead ? lead.name : "Unknown",
        );
      }
    } catch (e) {
      dispatch(showToast({ message: "No phone number found", type: "error" }));
    }
  };

  // ─── EMAIL HANDLERS ───
  const handleOpenSenderSelect = (leadId, leadEmail, leadName, e) => {
    e.stopPropagation();
    if (!emailSenderOptions || emailSenderOptions.length === 0) {
      dispatch(
        showToast({ message: "No email accounts assigned.", type: "error" }),
      );
      return;
    }
    setTargetLeadEmail({ id: leadId, email: leadEmail, name: leadName });
    setShowSenderSelect(true);
  };

  const handleSelectSender = (sender) => {
    setSelectedSender(sender);
    setShowSenderSelect(false);
    setComposeLeadName(targetLeadEmail.name);
    setComposeSubject("");
    setComposeBody("");
    setAttachments([]);
    setShowComposeEmail(true);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(
      (f) => f.type.startsWith("image/") || f.type === "application/pdf",
    );
    const newAttachments = validFiles.map((f) => ({
      name: f.name,
      type: f.type,
      size: (f.size / 1024).toFixed(1) + " KB",
      file: f,
    }));
    setAttachments((prev) => [...prev, ...newAttachments]);
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSendEmail = async () => {
    if (
      !composeLeadName ||
      !composeSubject ||
      !composeBody ||
      !selectedSender
    ) {
      dispatch(
        showToast({ message: "All fields are required", type: "error" }),
      );
      return;
    }
    setSendingEmail(true);
    try {
      const attachmentsText =
        attachments.length > 0
          ? `\n\n--- Attachments ---\n${attachments.map((a) => `${a.name} (${a.type})`).join("\n")}`
          : "";

      await adminApi.post("/admin/email/send", {
        to: targetLeadEmail.email,
        subject: composeSubject,
        body: composeBody + attachmentsText,
        senderId: selectedSender.id,
      });
      dispatch(
        showToast({ message: "Email sent successfully", type: "success" }),
      );
      setShowComposeEmail(false);
      setComposeLeadName("");
      setComposeSubject("");
      setComposeBody("");
      setSelectedSender(null);
      setAttachments([]);
    } catch (e) {
      dispatch(
        showToast({
          message: e.response?.data?.message || "Failed to send email",
          type: "error",
        }),
      );
    } finally {
      setSendingEmail(false);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      new_lead: "bg-emerald-700 text-white border-emerald-900",
      pending: "bg-amber-600 text-white border-amber-800",
      call_back: "bg-blue-700 text-white border-blue-900",
      potential_client: "bg-violet-800 text-white border-violet-950",
      low_potential_client: "bg-cyan-700 text-white border-cyan-900",
      not_interested: "bg-slate-600 text-white border-slate-800",
      wrong_number: "bg-rose-800 text-white border-rose-950",
      language_irrelevant: "bg-fuchsia-700 text-white border-fuchsia-900",
      no_answer: "bg-orange-600 text-white border-orange-800",
      underage: "bg-red-900 text-white border-red-950",
      not_registered: "bg-amber-800 text-white border-amber-950",
      on_call: "bg-amber-800 text-white border-amber-950",
      ftd: "bg-lime-700 text-white border-lime-900",
    };
    return (
      <span
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-mono font-extrabold uppercase tracking-wider border-b-[2px] ${colors[status] || "bg-[#94a3b8] text-white border-[#64748b]"}`}
      >
        {status ? status.replace(/_/g, " ").toUpperCase() : "PENDING"}
      </span>
    );
  };

  const getDepositBadge = (lead) => {
    if (lead.depositStatus === "deposited") {
      const dateStr = lead.depositDate
        ? new Date(lead.depositDate).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          })
        : "";
      return (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-[#ecfdf5] text-[#059669] text-[9px] font-mono font-extrabold border-2 border-[#a7f3d0]"
          title={`Deposited: ${dateStr}`}
        >
          <i className="fas fa-check-circle text-[8px]" /> DEPOSITED{" "}
          {dateStr && (
            <span className="ml-1 text-[7px] opacity-70">{dateStr}</span>
          )}
        </span>
      );
    }
    return null;
  };

  const getSourceBadge = (source) => {
    if (!source || source === "website")
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg bg-[#2563eb] text-white text-[9px] font-mono font-extrabold uppercase tracking-wider">
          WEBSITE
        </span>
      );
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg bg-[#7c3aed] text-white text-[9px] font-mono font-extrabold uppercase tracking-wider">
        {source}
      </span>
    );
  };

  const formatDate = (dateStr) =>
    !dateStr
      ? "—"
      : new Date(dateStr).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
  const formatDateTime = (dateStr) =>
    !dateStr
      ? "—"
      : new Date(dateStr).toLocaleString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });

  const Pagination = () => {
    if (totalPages <= 1) return null;
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return (
      <div className="flex items-center justify-between px-5 py-3.5 border-t-2 border-[#e2e8f0] bg-[#f8fafc]">
        <span className="text-[11px] text-[#475569] font-mono font-extrabold">
          Showing {(currentPage - 1) * LEADS_PER_PAGE + 1}–
          {Math.min(currentPage * LEADS_PER_PAGE, displayLeads.length)} of{" "}
          <span className="text-[#0f172a]">{displayLeads.length}</span> leads
        </span>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => goToPage(1)}
            disabled={currentPage === 1}
            className="w-8 h-8 rounded-lg bg-white border-2 border-[#e2e8f0] text-[#64748b] text-[11px] font-extrabold flex items-center justify-center hover:text-[#1e3a5f] hover:border-[#1e3a5f] disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <i className="fas fa-angles-left text-[9px]" />
          </button>
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="w-8 h-8 rounded-lg bg-white border-2 border-[#e2e8f0] text-[#64748b] text-[11px] font-extrabold flex items-center justify-center hover:text-[#1e3a5f] hover:border-[#1e3a5f] disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <i className="fas fa-chevron-left text-[9px]" />
          </button>
          {pages.map((page) => (
            <button
              key={page}
              onClick={() => goToPage(page)}
              className={`w-8 h-8 rounded-lg text-[11px] font-mono font-extrabold transition-all ${currentPage === page ? "bg-[#1e3a5f] text-white border-2 border-[#1e3a5f] shadow-md" : "bg-white border-2 border-[#e2e8f0] text-[#64748b] hover:text-[#1e3a5f] hover:border-[#1e3a5f]"}`}
            >
              {page}
            </button>
          ))}
          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="w-8 h-8 rounded-lg bg-white border-2 border-[#e2e8f0] text-[#64748b] text-[11px] font-extrabold flex items-center justify-center hover:text-[#1e3a5f] hover:border-[#1e3a5f] disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <i className="fas fa-chevron-right text-[9px]" />
          </button>
          <button
            onClick={() => goToPage(totalPages)}
            disabled={currentPage === totalPages}
            className="w-8 h-8 rounded-lg bg-white border-2 border-[#e2e8f0] text-[#64748b] text-[11px] font-extrabold flex items-center justify-center hover:text-[#1e3a5f] hover:border-[#1e3a5f] disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <i className="fas fa-angles-right text-[9px]" />
          </button>
        </div>
      </div>
    );
  };

  if (isLoading)
    return (
      <div className="flex justify-center py-20">
        <div className="w-10 h-10 border-[3px] border-[#1e3a5f] border-t-transparent rounded-full animate-spin" />
      </div>
    );

  return (
    <div>
      <audio ref={remoteAudioRef} autoPlay />
      <audio ref={localAudioRef} autoPlay muted />

      <div className="flex flex-wrap items-center justify-between gap-4 p-5 border-b-2 border-[#e2e8f0] bg-[#f8fafc]">
        <div>
          <h2 className="font-syne text-xl font-extrabold text-[#0b1e33] uppercase tracking-tight">
            MY LEADS
          </h2>
          <p className="text-[11px] text-[#475569] font-mono font-extrabold mt-1 uppercase tracking-wider">
            {displayLeads.length} LEAD{displayLeads.length !== 1 ? "S" : ""}{" "}
            FOUND
          </p>
        </div>
        <form onSubmit={handleSearch} className="flex items-center gap-2">
          <div className="relative">
            <i className="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-[#94a3b8] text-[11px]" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                if (!e.target.value.trim()) {
                  setSearchResult(null);
                  setCurrentPage(1);
                }
              }}
              placeholder="Search by ID, name, email..."
              className="w-56 bg-white border-2 border-[#cbd5e1] rounded-xl py-2.5 pl-10 pr-10 text-[#0f172a] text-[11px] font-extrabold outline-none focus:border-[#1e3a5f] transition-all placeholder:text-[#94a3b8]"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94a3b8] hover:text-[#dc2626] text-[11px]"
              >
                <i className="fas fa-times" />
              </button>
            )}
          </div>
          <button
            type="submit"
            disabled={isSearching || !searchQuery.trim()}
            className="px-5 py-2.5 rounded-xl bg-[#1e3a5f] text-white text-[11px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] disabled:opacity-50 disabled:cursor-not-allowed transition-all border-b-[2px] border-[#0b1e33]"
          >
            {isSearching ? (
              <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              "SEARCH"
            )}
          </button>
        </form>
        <button
          onClick={fetchLeads}
          className="px-4 py-2 rounded-xl bg-[#1e3a5f] text-white text-[10px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] transition-all border-b-[2px] border-[#0b1e33]"
        >
          <i className="fas fa-sync-alt mr-1.5" /> REFRESH
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3 px-5 py-3 border-b-2 border-[#e2e8f0] bg-[#f8fafc]">
        <span className="text-[10px] text-[#475569] font-mono font-extrabold uppercase tracking-wider">
          FILTERS:
        </span>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-white border-2 border-[#cbd5e1] rounded-xl px-3 py-2 text-[#0f172a] text-[10px] font-extrabold outline-none focus:border-[#1e3a5f] transition-all cursor-pointer"
        >
          <option value="all">ALL STATUSES</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s.replace(/_/g, " ").toUpperCase()}
            </option>
          ))}
        </select>
        <div className="flex items-center gap-1.5">
          <span className="text-[9px] text-[#64748b] font-mono font-bold uppercase">
            From:
          </span>
          <input
            type="date"
            value={startDateFilter}
            onChange={(e) => setStartDateFilter(e.target.value)}
            className="bg-white border-2 border-[#cbd5e1] rounded-xl px-3 py-2 text-[#0f172a] text-[10px] font-extrabold outline-none focus:border-[#1e3a5f] transition-all"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[9px] text-[#64748b] font-mono font-bold uppercase">
            To:
          </span>
          <input
            type="date"
            value={endDateFilter}
            onChange={(e) => setEndDateFilter(e.target.value)}
            className="bg-white border-2 border-[#cbd5e1] rounded-xl px-3 py-2 text-[#0f172a] text-[10px] font-extrabold outline-none focus:border-[#1e3a5f] transition-all"
          />
        </div>
        {(statusFilter !== "all" || startDateFilter || endDateFilter) && (
          <button
            onClick={() => {
              setStatusFilter("all");
              setStartDateFilter("");
              setEndDateFilter("");
              setCurrentPage(1);
            }}
            className="text-[10px] text-[#dc2626] font-mono font-extrabold hover:underline uppercase tracking-wider"
          >
            <i className="fas fa-times mr-1" />
            CLEAR FILTERS
          </button>
        )}
        <span className="ml-auto text-[10px] text-[#475569] font-mono font-extrabold">
          {displayLeads.length} LEAD{displayLeads.length !== 1 ? "S" : ""} FOUND
        </span>
      </div>

      {displayLeads.length === 0 ? (
        <div className="text-center py-20">
          <div className="w-20 h-20 rounded-2xl bg-[#f1f5f9] flex items-center justify-center mx-auto mb-4">
            <i
              className={`fas ${searchResult !== null || statusFilter !== "all" || startDateFilter || endDateFilter ? "fa-filter" : "fa-users"} text-[#94a3b8] text-3xl`}
            />
          </div>
          <p className="text-[#475569] font-syne font-extrabold text-base uppercase tracking-wider mb-1">
            {searchResult !== null
              ? "NO LEADS FOUND"
              : statusFilter !== "all" || startDateFilter || endDateFilter
                ? "NO LEADS MATCH FILTERS"
                : "NO LEADS ASSIGNED"}
          </p>
          {searchResult !== null ||
          statusFilter !== "all" ||
          startDateFilter ||
          endDateFilter ? (
            <button
              onClick={() => {
                clearSearch();
                setStatusFilter("all");
                setStartDateFilter("");
                setEndDateFilter("");
              }}
              className="mt-2 text-[#1e3a5f] text-xs font-syne font-extrabold hover:underline uppercase tracking-wider"
            >
              CLEAR ALL FILTERS
            </button>
          ) : (
            <p className="text-[#64748b] text-[11px] font-mono font-bold mt-1">
              Contact admin to get leads assigned
            </p>
          )}
        </div>
      ) : (
        <>
          <div className="divide-y-2 divide-[#f1f5f9]">
            {paginatedLeads.map((lead) => (
              <div
                key={lead.id}
                className="p-4 bg-white hover:bg-[#f8fafc] transition-all"
              >
                <div
                                    className="flex items-start justify-between gap-4 cursor-pointer"
                  onClick={() =>
                    setExpandedLead(expandedLead === lead.id ? null : lead.id)
                  }
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#0b1e33] to-[#1e3a5f] flex items-center justify-center flex-shrink-0 shadow-md shadow-[#1e3a5f]/25">
                        <span className="text-[11px] font-extrabold text-white font-mono">
                          {lead.short_id || lead.name?.[0]?.toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <div className="font-syne text-sm font-extrabold text-[#0f172a] flex items-center gap-2">
                          {lead.name}
                          {lead.short_id && (
                            <span className="text-[10px] font-mono font-extrabold text-[#1e3a5f] bg-[#eff6ff] border-2 border-[#bfdbfe] px-2 py-0.5 rounded-lg">
                              #{lead.short_id}
                            </span>
                          )}
                          {/* Show a badge if it's a Retention Data lead */}
                          {isRetentionData(lead) && (
                            <span className="text-[9px] font-mono font-extrabold text-[#92400e] bg-[#fef3c7] border border-[#fcd34d] px-1.5 py-0.5 rounded">
                              RETENTION DATA
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-[#64748b] font-mono font-bold flex items-center gap-1 flex-wrap">
                          {canSeeFullDetails() ? (
                            <span>{lead.email}</span>
                          ) : (
                            <span>****@****</span>
                          )}
                          <span>·</span>
                          {canSeeFullDetails() ? (
                            <span>{lead.phone}</span>
                          ) : (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={(e) => handleDialNumber(lead.id, e)}
                                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-[#ecfdf5] text-[#059669] border border-[#a7f3d0] hover:bg-[#d1fae5] transition-all font-extrabold"
                                title="Click to Call"
                              >
                                <i className="fas fa-phone-alt text-[9px]" />{" "}
                                CALL
                              </button>
                              <button
                                onClick={(e) =>
                                  handleOpenSenderSelect(
                                    lead.id,
                                    lead.email,
                                    lead.name,
                                    e,
                                  )
                                }
                                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-[#eff6ff] text-[#2563eb] border border-[#bfdbfe] hover:bg-[#dbeafe] transition-all font-extrabold"
                                title="Click to Email"
                              >
                                <i className="fas fa-envelope text-[9px]" />{" "}
                                EMAIL
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-[52px] text-[9px] flex-wrap mt-1.5">
                      {getStatusBadge(lead.status)}
                      {getSourceBadge(lead.source)}
                      {getDepositBadge(lead)}
                      {lead.linked_user_id && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-[#ecfdf5] text-[#059669] text-[9px] font-mono font-extrabold border-2 border-[#a7f3d0]">
                          <i className="fas fa-link mr-0.5 text-[8px]" /> USER:{" "}
                          {lead.short_id}
                        </span>
                      )}
                      <span className="text-[#64748b] font-mono font-bold">
                        <i className="fas fa-calendar-plus mr-0.5 text-[8px]" />
                        {formatDate(lead.createdAt)}
                      </span>
                      {lead.closedAt && (
                        <span className="text-[#64748b] font-mono font-bold">
                          <i className="fas fa-calendar-check mr-0.5 text-[8px]" />
                          Closed: {formatDate(lead.closedAt)}
                        </span>
                      )}
                      {!lead.closedAt && (
                        <span className="text-[#64748b] font-mono font-bold">
                          <i className="fas fa-clock mr-0.5 text-[8px] text-[#d97706]" />
                          Open
                        </span>
                      )}
                    </div>
                  </div>
                  <i
                    className={`fas fa-chevron-${expandedLead === lead.id ? "up" : "down"} text-[#64748b] text-xs mt-2`}
                  />
                </div>

                {expandedLead === lead.id && (
                  <div className="ml-[52px] mt-4 space-y-4 border-t-2 border-[#e2e8f0] pt-4">
                    <div className="flex flex-wrap gap-4 text-[10px] text-[#475569] font-mono font-bold">
                      <span>
                        <i className="fas fa-calendar-plus mr-1 text-[#1e3a5f]" />
                        Created: {formatDateTime(lead.createdAt)}
                      </span>
                      {lead.closedAt && (
                        <span>
                          <i className="fas fa-calendar-check mr-1 text-[#dc2626]" />
                          Closed: {formatDateTime(lead.closedAt)}
                        </span>
                      )}
                      {!lead.closedAt && (
                        <span>
                          <i className="fas fa-clock mr-1 text-[#d97706]" />
                          Open
                        </span>
                      )}
                      <span>
                        <i className="fas fa-tag mr-1 text-[#1e3a5f]" />
                        ID: {lead.short_id || "—"}
                      </span>
                      {lead.linked_user_id && (
                        <span>
                          <i className="fas fa-link mr-1 text-[#059669]" />
                          Linked User: {lead.short_id}
                        </span>
                      )}
                      {lead.depositStatus === "deposited" &&
                        lead.depositDate && (
                          <span>
                            <i className="fas fa-coins mr-1 text-[#d97706]" />
                            Deposit: {formatDate(lead.depositDate)}
                          </span>
                        )}
                    </div>

                    {lead.linked_user_id ? (
                      <div className="p-4 rounded-xl bg-[#eff6ff] border-2 border-[#bfdbfe]">
                        <div className="text-[11px] text-[#1e3a5f] font-mono font-extrabold mb-2 flex items-center gap-2 uppercase tracking-wider">
                          <i className="fas fa-user" /> LINKED ACCOUNT
                        </div>
                        <p className="text-[10px] text-[#475569] font-mono font-bold mb-3">
                          <i className="fas fa-phone mr-1.5 text-[#1e3a5f]" />{" "}
                          Call the user → verify details → assist with KYC &
                          deposit
                        </p>
                        <div className="flex items-center gap-2 flex-wrap">
                          <button
                            onClick={() => handleViewLinkedUser(lead.id)}
                            className="px-4 py-2 rounded-xl bg-[#1e3a5f] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] transition-all border-b-[2px] border-[#0b1e33]"
                          >
                            <i className="fas fa-eye mr-1.5" /> QUICK VIEW
                          </button>
                          <button
                            onClick={() =>
                              window.open(
                                `/admin/user/${lead.linked_user_id}`,
                                "_blank",
                              )
                            }
                            className="px-4 py-2 rounded-xl bg-[#1e3a5f] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] transition-all border-b-[2px] border-[#0b1e33]"
                          >
                            <i className="fas fa-external-link-alt mr-1.5" />{" "}
                            FULL USER DETAILS
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="p-4 rounded-xl bg-[#fef9c3] border-2 border-[#fde047]">
                        <p className="text-[10px] text-[#92400e] font-mono font-bold">
                          <i className="fas fa-info-circle mr-1.5 text-[#d97706]" />{" "}
                          Account not created yet. Admin will create the
                          account. Update lead status to notify admin.
                        </p>
                      </div>
                    )}

                    {lead.comments && lead.comments.length > 0 && (
                      <div className="space-y-2 mb-2">
                        <div className="text-[9px] text-[#475569] font-mono font-extrabold uppercase tracking-wider">
                          HISTORY
                        </div>
                        {lead.comments.map((c) => (
                          <div
                            key={c.id}
                            className="flex items-start gap-2 text-[10px] bg-[#f8fafc] rounded-lg p-2.5 border border-[#e2e8f0]"
                          >
                            <span className="text-[#1e3a5f] font-extrabold whitespace-nowrap">
                              {c.staffName}:
                            </span>
                            <span className="text-[#475569] font-bold">
                              {c.comment}
                            </span>
                            {c.status && (
                              <span className="text-[#64748b] ml-auto whitespace-nowrap font-bold">
                                → {c.status.replace(/_/g, " ").toUpperCase()}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex items-start gap-2">
                      <select
                        value={statusInputs[lead.id] || ""}
                        onChange={(e) => {
                          setStatusInputs({
                            ...statusInputs,
                            [lead.id]: e.target.value,
                          });
                          setCustomStatus({ ...customStatus, [lead.id]: "" });
                        }}
                        className="bg-white border-2 border-[#cbd5e1] rounded-xl px-3 py-2.5 text-[#0f172a] text-[10px] font-extrabold outline-none focus:border-[#1e3a5f] w-[150px] flex-shrink-0 cursor-pointer"
                      >
                        <option value="">SELECT STATUS...</option>
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s} value={s}>
                            {s.replace(/_/g, " ").toUpperCase()}
                          </option>
                        ))}
                      </select>
                      <input
                        type="text"
                        value={customStatus[lead.id] || ""}
                        onChange={(e) => {
                          setCustomStatus({
                            ...customStatus,
                            [lead.id]: e.target.value,
                          });
                          setStatusInputs({ ...statusInputs, [lead.id]: "" });
                        }}
                        placeholder="Or write custom..."
                        className="flex-1 bg-white border-2 border-[#cbd5e1] rounded-xl px-4 py-2.5 text-[#0f172a] text-[10px] font-extrabold outline-none focus:border-[#1e3a5f] placeholder:text-[#94a3b8]"
                      />
                      <textarea
                        value={commentInputs[lead.id] || ""}
                        onChange={(e) =>
                          setCommentInputs({
                            ...commentInputs,
                            [lead.id]: e.target.value,
                          })
                        }
                        placeholder="Add comment..."
                        rows={1}
                        className="flex-[2] bg-white border-2 border-[#cbd5e1] rounded-xl px-4 py-2.5 text-[#0f172a] text-[10px] font-extrabold outline-none focus:border-[#1e3a5f] resize-none placeholder:text-[#94a3b8]"
                      />
                      <button
                        onClick={() => handleUpdate(lead.id)}
                        disabled={updating[lead.id]}
                        className="px-5 py-2.5 rounded-xl bg-[#1e3a5f] text-white text-[10px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] disabled:opacity-50 transition-all whitespace-nowrap border-b-[3px] border-[#0b1e33]"
                      >
                        {updating[lead.id] ? (
                          <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          "UPDATE"
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          <Pagination />
        </>
      )}

      {showUserModal && selectedUser && (
        <UserDetailModal
          user={selectedUser}
          leadInfo={leads.find((l) => l.linked_user_id === selectedUser.id)}
          onClose={() => {
            setShowUserModal(false);
            setSelectedUser(null);
          }}
        />
      )}

      {/* SENDER SELECT POPUP (50% SCREEN) */}
      {showSenderSelect && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          onClick={() => setShowSenderSelect(false)}
        >
          <div
            className="bg-white w-1/2 max-w-2xl rounded-2xl shadow-2xl overflow-hidden border border-[#e2e8f0] animate-bounce-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-8 py-6 bg-gradient-to-r from-[#0b1e33] to-[#1e3a5f] text-white flex items-center justify-between">
              <div>
                <h3 className="font-syne text-2xl font-extrabold uppercase tracking-tight flex items-center gap-3">
                  <i className="fas fa-envelope text-[#a7f3d0]" /> Select Sender
                </h3>
                <p className="text-[12px] font-mono text-[#bfdbfe] mt-1">
                  Choose which email account to send from
                </p>
              </div>
              <button
                onClick={() => setShowSenderSelect(false)}
                className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all"
              >
                <i className="fas fa-times text-lg" />
              </button>
            </div>
            <div className="p-8 space-y-4 max-h-[60vh] overflow-y-auto">
              {emailSenderOptions.map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => handleSelectSender(opt)}
                  className="w-full text-left px-6 py-5 rounded-2xl border-2 border-[#e2e8f0] hover:border-[#1e3a5f] hover:bg-[#eff6ff] hover:shadow-md transition-all flex items-center gap-5 group"
                >
                  <div className="w-14 h-14 rounded-2xl bg-[#eff6ff] group-hover:bg-white flex items-center justify-center flex-shrink-0 transition-all border-2 border-[#bfdbfe]">
                    <i className="fas fa-envelope text-[#1e3a5f] text-xl" />
                  </div>
                  <div>
                    <div className="font-syne text-lg font-extrabold text-[#0f172a]">
                      {opt.name}
                    </div>
                    <div className="text-[11px] font-mono text-[#64748b] mt-0.5">
                      Click to use this sender
                    </div>
                  </div>
                  <i className="fas fa-arrow-right ml-auto text-[#94a3b8] group-hover:text-[#1e3a5f] transition-all" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* COMPOSE EMAIL MODAL (GMAIL STYLE) */}
      {showComposeEmail && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          onClick={() => setShowComposeEmail(false)}
        >
          <div
            className="bg-white w-full max-w-[900px] h-[85vh] flex flex-col rounded-2xl shadow-2xl overflow-hidden border border-[#e2e8f0] animate-bounce-in"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-4 bg-[#f2f6fc] border-b border-[#e2e8f0] flex items-center justify-between flex-shrink-0">
              <h3 className="font-syne text-xl font-extrabold text-[#0f172a] tracking-tight flex items-center gap-3">
                <i className="fas fa-pen text-[#1e3a5f]" /> New Message
              </h3>
              <button
                onClick={() => setShowComposeEmail(false)}
                className="w-10 h-10 rounded-full hover:bg-[#e2e8f0] flex items-center justify-center transition-all text-[#64748b]"
              >
                <i className="fas fa-times text-lg" />
              </button>
            </div>

            {/* Form Fields */}
            <div className="flex-1 overflow-y-auto flex flex-col">
              <div className="flex items-center border-b border-[#e2e8f0] px-6 py-3">
                <span className="text-sm text-[#64748b] w-20 font-medium">
                  From
                </span>
                <div className="flex-1 text-sm font-bold text-[#0f172a] flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-[#1e3a5f] text-white flex items-center justify-center text-[10px]">
                    {selectedSender?.name?.[0]?.toUpperCase()}
                  </div>
                  {selectedSender?.name}
                </div>
              </div>

              <div className="flex items-center border-b border-[#e2e8f0] px-6 py-3">
                <span className="text-sm text-[#64748b] w-20 font-medium">
                  To
                </span>
                <div className="flex-1 text-sm font-bold text-[#0f172a] flex items-center gap-2">
                  <span className="bg-[#eff6ff] border border-[#bfdbfe] px-3 py-1 rounded-full text-[#1e3a5f] flex items-center gap-2">
                    {composeLeadName}
                  </span>
                </div>
              </div>

              <div className="flex items-center border-b border-[#e2e8f0] px-6 py-3">
                <span className="text-sm text-[#64748b] w-20 font-medium">
                  Subject
                </span>
                <input
                  value={composeSubject}
                  onChange={(e) => setComposeSubject(e.target.value)}
                  placeholder="Subject"
                  className="flex-1 text-sm font-bold text-[#0f172a] outline-none placeholder:text-[#94a3b8] placeholder:font-normal"
                />
              </div>

              {/* Attachments Preview */}
              {attachments.length > 0 && (
                <div className="px-6 py-3 border-b border-[#e2e8f0] bg-[#f8fafc] flex flex-wrap gap-2">
                  {attachments.map((file, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2 bg-white border border-[#cbd5e1] rounded-lg px-3 py-1.5 shadow-sm"
                    >
                      <i
                        className={`fas ${file.type.includes("image") ? "fa-image text-[#2563eb]" : "fa-file-pdf text-[#dc2626]"} text-sm`}
                      />
                      <span className="text-xs font-medium text-[#0f172a] max-w-[150px] truncate">
                        {file.name}
                      </span>
                      <span className="text-[9px] text-[#64748b] font-mono">
                        {file.size}
                      </span>
                      <button
                        onClick={() => removeAttachment(idx)}
                        className="ml-2 text-[#94a3b8] hover:text-[#dc2626]"
                      >
                        <i className="fas fa-times text-xs" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Toolbar */}
              <div className="flex items-center gap-1 px-6 py-2 border-b border-[#e2e8f0] bg-white flex-shrink-0">
                <button className="w-9 h-9 rounded hover:bg-[#f1f5f9] flex items-center justify-center text-[#475569] transition-all">
                  <i className="fas fa-bold text-sm" />
                </button>
                <button className="w-9 h-9 rounded hover:bg-[#f1f5f9] flex items-center justify-center text-[#475569] transition-all">
                  <i className="fas fa-italic text-sm" />
                </button>
                <button className="w-9 h-9 rounded hover:bg-[#f1f5f9] flex items-center justify-center text-[#475569] transition-all">
                  <i className="fas fa-underline text-sm" />
                </button>
                <div className="w-px h-6 bg-[#e2e8f0] mx-1" />
                <button className="w-9 h-9 rounded hover:bg-[#f1f5f9] flex items-center justify-center text-[#475569] transition-all">
                  <i className="fas fa-list-ul text-sm" />
                </button>
                <button className="w-9 h-9 rounded hover:bg-[#f1f5f9] flex items-center justify-center text-[#475569] transition-all">
                  <i className="fas fa-list-ol text-sm" />
                </button>
                <div className="w-px h-6 bg-[#e2e8f0] mx-1" />
                <button className="w-9 h-9 rounded hover:bg-[#f1f5f9] flex items-center justify-center text-[#475569] transition-all">
                  <i className="fas fa-link text-sm" />
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-9 h-9 rounded hover:bg-[#f1f5f9] flex items-center justify-center text-[#475569] transition-all"
                  title="Attach Image or PDF"
                >
                  <i className="fas fa-paperclip text-sm" />
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  multiple
                  accept="image/*,application/pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              <textarea
                value={composeBody}
                onChange={(e) => setComposeBody(e.target.value)}
                placeholder="Write your message here..."
                className="flex-1 w-full p-6 text-sm text-[#0f172a] outline-none resize-none placeholder:text-[#94a3b8] font-sans leading-relaxed"
              />
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-[#f2f6fc] border-t border-[#e2e8f0] flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2">
                <button
                  onClick={handleSendEmail}
                  disabled={sendingEmail}
                  className="px-8 py-3 rounded-full bg-[#1e3a5f] text-white text-sm font-medium hover:bg-[#0b1e33] disabled:opacity-50 transition-all flex items-center gap-2 shadow-md"
                >
                  {sendingEmail ? (
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <i className="fas fa-paper-plane" />
                  )}
                  Send
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-10 h-10 rounded-full hover:bg-[#e2e8f0] flex items-center justify-center text-[#475569] transition-all"
                  title="Attach file"
                >
                  <i className="fas fa-paperclip" />
                </button>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowComposeEmail(false)}
                  className="w-10 h-10 rounded-full hover:bg-[#e2e8f0] flex items-center justify-center text-[#475569] transition-all"
                  title="Discard"
                >
                  <i className="fas fa-trash-alt" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CUSTOM INCOMING CALL WIDGET (no number shown) */}
      {incomingCall && (
        <div
          style={{ position: "fixed", bottom: 20, right: 20, zIndex: 10000 }}
          className="w-[320px] bg-white border-2 border-[#1e3a5f] rounded-2xl shadow-2xl overflow-hidden animate-bounce-in"
        >
          <div className="px-4 py-3 bg-[#0b1e33] text-white">
            <div className="text-[9px] font-mono font-extrabold uppercase tracking-wider text-[#a7f3d0] flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#a7f3d0] animate-pulse" />{" "}
              INCOMING CALL
            </div>
            <div className="font-syne text-base font-extrabold mt-1 truncate">
              {incomingCall.lead?.name || "Unknown caller"}
            </div>
            {incomingCall.lead?.short_id && (
              <div className="text-[10px] font-mono font-bold text-[#bfdbfe]">
                #{incomingCall.lead.short_id}
              </div>
            )}
          </div>
          <div className="flex gap-2 p-3">
            <button
              onClick={declineCall}
              className="flex-1 py-2.5 rounded-xl bg-[#dc2626] text-white text-[11px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#b91c1c] border-b-[3px] border-[#b91c1c]"
            >
              <i className="fas fa-phone-slash mr-1.5" /> DECLINE
            </button>
            <button
              onClick={acceptCall}
              className="flex-1 py-2.5 rounded-xl bg-[#059669] text-white text-[11px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#047857] border-b-[3px] border-[#047857]"
            >
              <i className="fas fa-phone-alt mr-1.5" /> ACCEPT
            </button>
          </div>
        </div>
      )}

      {/* CUSTOM END CALL BUTTON (outgoing calls) */}
      {isCallActive && !incomingCall && (
        <div style={{ position: "fixed", bottom: 20, right: 20, zIndex: 9999 }}>
          <button
            onClick={handleHangup}
            className="px-6 py-3 rounded-full bg-[#dc2626] text-white font-syne font-extrabold uppercase tracking-wider shadow-2xl hover:bg-[#b91c1c] transition-all border-b-[3px] border-[#b91c1c] flex items-center gap-2"
          >
            <i className="fas fa-phone-slash text-lg" /> END CALL
          </button>
        </div>
      )}
    </div>
  );
};

export default StaffLeadPanel;